import Box from '@mui/material/Box';
import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { motion } from 'framer-motion';
import { buttonHover } from '@/theme/motion';

export type ButtonIntent = 'primary-dark' | 'primary-gold' | 'secondary-outlined' | 'ghost';

export type ButtonProps = Omit<MuiButtonProps, 'color' | 'variant'> & {
  disableMotion?: boolean;
  to?: string;
  intent?: ButtonIntent;
  color?: MuiButtonProps['color'];
  variant?: MuiButtonProps['variant'];
};

function resolveButtonProps(intent: ButtonIntent = 'primary-dark'): Pick<MuiButtonProps, 'variant' | 'color'> {
  switch (intent) {
    case 'primary-gold':
      return { variant: 'contained', color: 'secondary' };
    case 'secondary-outlined':
      return { variant: 'outlined', color: 'primary' };
    case 'ghost':
      return { variant: 'text', color: 'primary' };
    case 'primary-dark':
    default:
      return { variant: 'contained', color: 'primary' };
  }
}

export function Button({
  disableMotion,
  fullWidth,
  intent = 'primary-dark',
  variant,
  color,
  sx,
  ...props
}: ButtonProps) {
  const resolved = resolveButtonProps(intent);
  const button = (
    <MuiButton
      fullWidth={fullWidth}
      variant={variant ?? resolved.variant}
      color={color ?? resolved.color}
      sx={sx}
      {...props}
    />
  );

  if (disableMotion) {
    return button;
  }

  return (
    <Box
      component={motion.div}
      whileHover={buttonHover}
      whileTap={{ scale: 0.98 }}
      sx={{
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {button}
    </Box>
  );
}
